import React, { useMemo, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import api from '@galo-monitor/services/apis/galoMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyActions } from '@galo-monitor/stores/company'
import colors from '@galo-monitor/styles/colors'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import CompanyModal from '@galo-monitor/web-containers/modals/root/CompanyModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const CompaniesScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()
  const tableRef = useRef(null)

  const asyncCompaniesData = useCallback(
    params => api.get(
      '/admin/companies',
      {
        params
      }
    ),
    []
  )

  const userActions = useMemo(
    () => getActionsBySlug(routes.companies.slug),
    [getActionsBySlug, routes.companies.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'nameCompany',
        render: columnData => (
          <Link
            to={ `/companies/${ columnData.id }` }
            style={ {
              color: colors.primary, textDecoration: 'underline'
            } }
          >
            {columnData.nameCompany}
          </Link>
        )
      },
      {
        title: t('name of {this}', { gender: 'male', howMany: 1, this: t('responsible') }),
        field: 'nameResponsible'
      },
      {
        title: t('email'),
        field: 'emailResponsible'
      },
      {
        title: t('main phone'),
        field: 'phoneResponsible'
      }
    ],
    [t]
  )

  const onSuccess = useCallback(() => {
    tableRef.current.onQueryChange()
  }, [])

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-company',
        Component: CompanyModal,
        props: {
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onEditClick = useCallback(
    (_, company) => {
      createDialog({
        id: 'edit-company',
        Component: CompanyModal,
        props: {
          company,
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onSoftDeleteClick = useCallback(
    (_, company) => {
      const isActiveCompany = company.isActive
      const actionText = t(isActiveCompany ? 'inactivate' : 'activate')
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('company', { howMany: 1 }),
            action: actionText,
            name: company.nameCompany
          }),
          buttonTitle: actionText,
          buttonProps: {
            color: isActiveCompany ? 'error' : 'primary'
          },
          onConfirm: () => {
            dispatch(CompanyActions.removeCompany(
              company,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('company', { howMany: 1 }),
                  action: toLower(t(isActiveCompany ? 'inactivated' : 'activated', { gender: 'female', howMany: 1 }))
                }))
                onSuccess()
              },
              () => {
                createDialog({
                  id: 'company-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: actionText,
                      this: toLower(t('company', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('exist farms or modules attached to this company')  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, onSuccess, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          rowData => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          rowData => ({
            icon: rowData.isActive
              ? DataTableIcons.Delete
              : () => <DataTableIcons.Reply style={ { color: colors.red } } />,
            tooltip: rowData.isActive
              ? t('datagrid body inactivate tooltip')
              : t('datagrid body activate tooltip'),
            onClick: onSoftDeleteClick,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [onEditClick, onSoftDeleteClick, t, userActions.delete, userActions.edit]
  )

  const crumbs = useMemo(() => [
    {
      title: t('company', { howMany: 2 }),
      key: 'companies-screen',
      url: '/companies',
      active: true
    }
  ], [t])

  return (
    <Screen title={ routes.companies.title } breadcrumbs={ crumbs }>
      <DataTable
        tableRef={ tableRef }
        data={ asyncCompaniesData }
        columns={ columns }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        disabledRow={ rowData => !rowData.isActive }
        customActionsAtLast
        softDelete
      />
    </Screen>
  )
}

export default CompaniesScreen
